import { derivePageSides, deriveSpreads, matchSpreadAnchor } from "./binding";
import {
  PublishingDocument,
  PublishingGeometry,
  PublishingPage,
  PublishingSection,
  PublishingSite,
  publishingDocumentSchema,
  validatePublishingReferences,
} from "./document";
import { resolveDigitalPresentation } from "./digital-presentation";
import { resolvePageBoxes } from "./boxes";
import {
  type ResolvedGrid,
  type ResolvedGuide,
  resolvePageLayout,
  resolveSpreadGuides,
} from "./guides";
import { resolveMasterAssignment } from "./masters";

export type PageRectangle = {
  x: number;
  y: number;
  width: number;
  height: number;
};
export type ResolvedPublishingPage<C = PublishingSite["components"][number]> = {
  id: string;
  /** Zero-based physical position; identical across label styles. */
  index: number;
  /** Physical folio number; identical across label styles. */
  number: number;
  /** Section-driven label; null when the section style is "none". */
  label: string | null;
  /** Governing section, or null before the first section start. */
  section: PublishingSection | null;
  side: "left" | "right";
  component: C | null;
  geometry: PublishingGeometry;
  /** Physical, side-resolved guides; renderers must not mirror them again. */
  guides: ResolvedGuide[];
  grids: ResolvedGrid[];
  printState: PublishingPage["printState"];
  masterItems?: {
    id: string;
    component: C;
    position: { x: number; y: number };
    layer: "below" | "above";
    printState: PublishingPage["printState"];
  }[];
  boxes: {
    media: PageRectangle;
    trim: PageRectangle;
    bleed: PageRectangle;
    content: PageRectangle;
  };
};

export { resolvePageBoxes } from "./boxes";

/** Pure composition: no ViewCtx, frame placement, active variant or mounted DOM dependency. */
export function resolvePublishingComposition<S extends PublishingSite>(
  site: S,
  input: PublishingDocument,
) {
  const doc = publishingDocumentSchema.parse(input);
  validatePublishingReferences(site, doc);
  type C = S["components"][number];
  // Sections govern labels only; index and number stay physical everywhere.
  const sides = derivePageSides(doc);
  const sectionStarts = new Map(
    (doc.sections ?? []).map((section) => [section.startPageId, section]),
  );
  let section: PublishingSection | null = null;
  let labelCount = doc.binding.startNumber;
  const pages: ResolvedPublishingPage<C>[] = doc.pages.map((page, index) => {
    const side = sides[index];
    const boundary = sectionStarts.get(page.id);
    if (boundary) {
      section = boundary;
      if (boundary.numbering.start !== "continue")
        labelCount = boundary.numbering.start;
    }
    let label: string | null = null;
    if (!section || section.numbering.style === "decimal") {
      label = String(labelCount);
      labelCount += 1;
    }
    const master = resolveMasterAssignment(doc, page);
    const geometry = master.geometry;
    const layout = resolvePageLayout(doc, page, side, geometry);
    return {
      id: page.id,
      index,
      number: doc.binding.startNumber + index,
      label,
      section,
      side,
      component:
        page.componentId === null
          ? null
          : (site.components.find((c) => c.uuid === page.componentId)! as C),
      geometry,
      ...(page.masterId
        ? {
            masterItems: master.items
              .filter((item) => item.side === "both" || item.side === side)
              .map((item) => ({
                id: item.id,
                component: site.components.find(
                  (c) => c.uuid === item.componentId,
                )! as C,
                position: item.position,
                layer: item.layer,
                printState: item.printState,
              })),
          }
        : {}),
      printState: page.printState,
      guides: layout.guides,
      grids: layout.grids,
      boxes: resolvePageBoxes(geometry, side),
    };
  });
  const resolvedById = new Map(pages.map((page) => [page.id, page]));
  const spreads = deriveSpreads(doc).map((spread) => {
    const left = spread.leftPageId
      ? resolvedById.get(spread.leftPageId)!
      : undefined;
    const right = spread.rightPageId
      ? resolvedById.get(spread.rightPageId)!
      : undefined;
    return {
      id: `spread-${spread.anchorPageId}`,
      ...(left ? { left } : {}),
      ...(right ? { right } : {}),
      guides: resolveSpreadGuides(
        doc.spreadGuides ?? [],
        { left, right },
        (guide) => {
          const match = matchSpreadAnchor(doc, guide.anchor);
          return "spread" in match && match.spread.anchorPageId === spread.anchorPageId;
        },
      ),
    };
  });
  return {
    version: 2 as const,
    title: doc.title,
    pages,
    spreads,
    /** Reader policy only; page geometry above stays print-owned. */
    digital: { presentation: resolveDigitalPresentation(doc) },
  };
}

export function hasPublishingPageContent(
  page: Pick<ResolvedPublishingPage, "component" | "masterItems">,
) {
  return !!page.component || !!page.masterItems?.length;
}
